/**
 * Client review mode (/marco2). Screens opened from the Marco 2 review page carry
 * `?revisao=cliente`; the flag is kept for the browser tab so prototype-only QA
 * tooling (the Admin role preview strip) stays out of the client's view.
 * It never changes permissions, rules or screen content.
 */
export const CLIENT_REVIEW_PARAM = 'revisao'
export const CLIENT_REVIEW_VALUE = 'cliente'
const STORAGE_KEY = 'beelyv.clientReview'

export function withClientReview(route: string): string {
  const [path, hash] = route.split('#')
  const joiner = path.includes('?') ? '&' : '?'
  return `${path}${joiner}${CLIENT_REVIEW_PARAM}=${CLIENT_REVIEW_VALUE}${hash ? `#${hash}` : ''}`
}

/** Host suffix of the shared (published) Magic Patterns Preview. */
const SHARED_PREVIEW_HOST_SUFFIX = '.magicpatterns.app'

/** True only on the shared Preview host — never in the editor preview or Screens canvas. */
export function isSharedPreviewHost(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname.endsWith(SHARED_PREVIEW_HOST_SUFFIX)
}

/** Custom client-review domain. On this host "/" is the Marco 2 entry and Home lives at /index. */
export const CLIENT_REVIEW_DOMAIN = 'preview.beelyv.com.br'

export function isClientReviewDomain(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname === CLIENT_REVIEW_DOMAIN
}

/** Route for the Mobile Home: /index on the client-review domain, "/" everywhere else. */
export function homePath(): string {
  return isClientReviewDomain() ? '/index' : '/'
}

/**
 * Prototype-only, deterministic entry for client review. Runs once on page load,
 * before the router reads the URL. No storage, no first-visit flag.
 * - preview.beelyv.com.br: pathname exactly "/" → /marco2.
 * - *.magicpatterns.app: pathname exactly "/" with no query and no hash → /marco2.
 * Every other host (editor preview, Screens canvas) and every other path is untouched.
 */
export function applyClientReviewEntry(): void {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = window.location
  if (pathname !== '/') return
  if (isClientReviewDomain()) {
    window.history.replaceState(window.history.state, '', '/marco2')
    return
  }
  if (isSharedPreviewHost() && !search && !hash) {
    window.history.replaceState(window.history.state, '', '/marco2')
  }
}

export function isClientReview(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(CLIENT_REVIEW_PARAM) === CLIENT_REVIEW_VALUE) {
      window.sessionStorage.setItem(STORAGE_KEY, '1')
      return true
    }
    return window.sessionStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}
